import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  mobileIntroActionsRecipe,
  mobileIntroBodyRecipe,
  mobileIntroHeadRecipe,
  mobileIntroRecipe,
} from '../recipes/mobile.recipe';
import { Stack } from './Layout';
import { Text } from './Text';

export interface MobileIntroProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Above the title: the product's mark. */
  brand?: React.ReactNode;
  title: React.ReactNode;
  /** The muted line under the title. */
  description?: React.ReactNode;
  /** `center` holds the title in the middle; `top` puts a list under it. */
  align?: 'center' | 'top';
  /**
   * Held along the bottom: the pill that answers the page, then a quieter
   * way out under it ("Skip onboarding").
   */
  actions?: React.ReactNode;
}

/**
 * A phone page that asks one thing before the app proper, as the Figma's
 * Welcome to Relay and setup checklist draw it: the brand, a centred title
 * over a muted line, the page's content, and its answers along the bottom.
 *
 *   <MobileIntro
 *     title="Welcome to Relay"
 *     description="Here's a few things to make your experience better"
 *     actions={<>
 *       <Button size="xl" pill variant="soft" onClick={next}>Continue</Button>
 *       <Button variant="link" onClick={skip}>Skip onboarding</Button>
 *     </>}
 *   >
 *     <IconFan items={…} />
 *   </MobileIntro>
 *
 * The signed-out landing page and the sign-up steps have no frame of their
 * own and are drawn on this too, so every page before the app reads alike.
 */
export const MobileIntro = React.forwardRef<HTMLDivElement, MobileIntroProps>(
  function MobileIntro(
    {
      brand,
      title,
      description,
      align = 'center',
      actions,
      className,
      children,
      ...props
    },
    ref
  ) {
    return (
      <div ref={ref} className={cn(mobileIntroRecipe(), className)} {...props}>
        <div className={mobileIntroBodyRecipe({ align })}>
          <div className={mobileIntroHeadRecipe()}>
            {brand}
            <Stack gap={2} align="center">
              <Text variant="h1" as="h1" align="center">
                {title}
              </Text>
              {description ? (
                <Text variant="body-lg" tone="muted" align="center">
                  {description}
                </Text>
              ) : null}
            </Stack>
          </div>
          {children}
        </div>
        {actions ? (
          <div className={mobileIntroActionsRecipe()}>{actions}</div>
        ) : null}
      </div>
    );
  }
);
